import { useState } from 'react';
import { toast } from 'sonner';
import { useLoaderData, useRevalidator } from 'react-router-dom';

import { SettingsSubPageLayout } from '../../components/settings/SettingsSubPageLayout';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { authAPI } from '../../data/auth';
import { settingsHubLoader } from '../../data/settings';
import pt from '../../locales/pt';
import { useAuth } from '../../store/AuthContext';
import { cn } from '../../utils/cn';

const EMPLOYMENT_OPTIONS = [
  'employee',
  'self_employed',
  'student',
  'retired',
  'other',
];

function DependentsStepper({ value, onChange }) {
  const adjust = (delta) => onChange(Math.max(0, Math.min(10, value + delta)));

  return (
    <div className="flex w-[150px] items-center overflow-hidden rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={() => adjust(-1)}
        aria-label="Menos"
        className="flex h-12 w-12 items-center justify-center text-sage-700 dark:text-sage-300"
      >
        –
      </button>
      <span className="flex-1 text-center font-display text-lg font-bold tabular-nums text-foreground">
        {value}
      </span>
      <button
        type="button"
        onClick={() => adjust(1)}
        aria-label="Mais"
        className="flex h-12 w-12 items-center justify-center text-sage-700 dark:text-sage-300"
      >
        +
      </button>
    </div>
  );
}

export function SettingsProfile() {
  const { user: loaderUser } = useLoaderData();
  const { refreshMe } = useAuth();
  const revalidator = useRevalidator();
  const profile = loaderUser?.profile;

  const [fullName, setFullName] = useState(profile?.full_name?.trim() || '');
  const [employmentType, setEmploymentType] = useState(profile?.employment_type || 'employee');
  const [dependents, setDependents] = useState(profile?.number_of_dependents ?? 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onSave = async () => {
    const trimmed = fullName.trim();
    if (trimmed.length < 2) {
      setError(pt.settings.profile.nameError);
      return;
    }
    setLoading(true);
    setError(null);
    const response = await authAPI.patchProfile({
      full_name: trimmed,
      employment_type: employmentType,
      has_dependents: dependents > 0,
      number_of_dependents: dependents,
    });
    setLoading(false);
    if (!response.success) {
      toast.error(response.message);
      return;
    }
    await refreshMe();
    revalidator.revalidate();
    toast.success(pt.settings.profile.saved);
  };

  return (
    <SettingsSubPageLayout
      section={pt.settings.profile.section}
      title={pt.settings.profile.title}
      subtitle={pt.settings.profile.subtitle}
      footer={
        <Button type="button" className="w-full" disabled={loading} onClick={onSave}>
          {pt.settings.profile.submit}
        </Button>
      }
    >
      <div>
        <Label htmlFor="settings-full-name">{pt.settings.profile.fullNameLabel}</Label>
        <Input
          id="settings-full-name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          autoComplete="name"
          className="mt-2"
        />
        <p className="mt-2 text-xs text-muted-foreground">{pt.settings.profile.fullNameHint}</p>
        {error ? <p className="mt-2 text-sm text-error">{error}</p> : null}
      </div>

      <div>
        <Label>{pt.settings.profile.employmentLabel}</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {EMPLOYMENT_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setEmploymentType(option)}
              className={cn(
                'rounded-[11px] border px-3.5 py-2 text-[13px] font-semibold transition-colors',
                employmentType === option
                  ? 'border-sage-600 bg-sage-50 text-sage-800 dark:border-sage-500 dark:bg-sage-950/40 dark:text-sage-200'
                  : 'border-border bg-card text-muted-foreground hover:bg-muted',
              )}
            >
              {pt.settings.profile.employmentOptions[option]}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{pt.settings.profile.employmentHint}</p>
      </div>

      <div>
        <Label>{pt.settings.profile.dependentsLabel}</Label>
        <div className="mt-2">
          <DependentsStepper value={dependents} onChange={setDependents} />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{pt.settings.profile.dependentsHint}</p>
      </div>

      <div className="flex gap-2.5 rounded-2xl border border-sage-100 bg-sage-50 p-3.5 dark:border-sage-900 dark:bg-sage-950/30">
        <div className="min-w-0">
          <p className="text-xs font-bold text-sage-800 dark:text-sage-200">
            {pt.settings.profile.whyTitle}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-sage-700 dark:text-sage-300">
            {pt.settings.profile.whyBody}
          </p>
        </div>
      </div>
    </SettingsSubPageLayout>
  );
}

SettingsProfile.loader = settingsHubLoader;
